// הקלטה קולית → WAV (PCM 16kHz מונו) לשליחה ל-Gemini.
//
// ⚠️ **לא MediaRecorder**: ב-Safari/iOS הוא מפיק פורמט שג'מיני לא תמיד מקבל. הדרך שעובדת
// (נבדקה בפועל באפליקציית Shopping של בר, ומועתקת משם) היא Web Audio גולמי + קידוד WAV
// בצד הלקוח. גודל: 16kHz · 16bit · מונו ≈ 32KB לשנייה, כלומר דקה ≈ 1.9MB — לכן יש תקרת זמן.

/** תקרת הקלטה. מעבר לזה המשתמשת כנראה שכחה לעצור, והמטען נעשה כבד לחינם. */
export const MAX_RECORD_MS = 60_000;

export interface Recorder {
  /** עוצר, משחרר את המיקרופון ומחזיר WAV */
  stop(): Promise<Blob>;
  /** שחרור בלי להפיק קובץ (ביטול/ניווט) */
  cancel(): void;
}

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const AudioCtx: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  processor.onaudioprocess = (e) => {
    chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  };
  source.connect(processor);
  // ה-processor לא כותב לפלט (יוצא שקט, בלי פידבק לרמקול) — אבל חייב חיבור ליעד כדי לרוץ
  processor.connect(ctx.destination);

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    processor.disconnect();
    source.disconnect();
    stream.getTracks().forEach((t) => t.stop());
  };

  return {
    cancel() {
      release();
      void ctx.close().catch(() => { /* כבר סגור */ });
    },
    async stop() {
      const rate = ctx.sampleRate;
      release();
      try { await ctx.close(); } catch { /* כבר סגור */ }
      const length = chunks.reduce((a, c) => a + c.length, 0);
      const data = new Float32Array(length);
      let off = 0;
      for (const c of chunks) { data.set(c, off); off += c.length; }
      return encodeWav(downsample(data, rate, 16000), 16000);
    },
  };
}

/** דגימה-מטה בממוצע חלון — פשוט ומספיק לדיבור; בלי זה המטען גדול פי 3. */
function downsample(buffer: Float32Array, inRate: number, outRate: number): Float32Array {
  if (outRate >= inRate) return buffer;
  const ratio = inRate / outRate;
  const out = new Float32Array(Math.round(buffer.length / ratio));
  let oi = 0, bi = 0;
  while (oi < out.length) {
    const next = Math.round((oi + 1) * ratio);
    let sum = 0, n = 0;
    for (let i = bi; i < next && i < buffer.length; i++) { sum += buffer[i]; n++; }
    out[oi++] = n ? sum / n : 0;
    bi = next;
  }
  return out;
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buf);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);              // PCM
  view.setUint16(22, 1, true);              // מונו
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true);              // block align
  view.setUint16(34, 16, true);             // bits per sample
  str(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (const s of samples) {
    const c = Math.max(-1, Math.min(1, s));
    view.setInt16(off, c < 0 ? c * 0x8000 : c * 0x7fff, true);
    off += 2;
  }
  return new Blob([view], { type: 'audio/wav' });
}

/** Blob → base64 בלי הקידומת `data:` */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('read_failed'));
    r.readAsDataURL(blob);
  });
}

/** mm:ss לטיימר ההקלטה */
export function fmtRecTime(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
